import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AvailableCompaniesService } from '../services/available-companies.service';

/** The server's code when the request names a company the caller does not belong to. */
export const NOT_CALLERS_COMPANY = 'customer_not_callers_company';

/** The machine code from an error body, whether it arrived parsed or as text. */
export function errorCodeOf(err: HttpErrorResponse): string | null {
  let body: unknown = err?.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  const code = (body as { error?: unknown } | null)?.error;
  return typeof code === 'string' ? code : null;
}

/**
 * On 403 customer_not_callers_company, refresh the company list.
 *
 * The server has just said the cached view of this user's companies is wrong.
 * Refreshing is the one thing that can make the switcher right again without
 * the user clearing storage. The error still reaches its caller unchanged: this
 * only refreshes, it never retries or hides the refusal.
 *
 * The service is fetched lazily: it makes HTTP calls itself, so injecting it in
 * the constructor would be a cycle through HTTP_INTERCEPTORS.
 */
@Injectable()
export class CompanyRefreshInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 403 &&
          errorCodeOf(err) === NOT_CALLERS_COMPANY &&
          !req.url.includes('GetCustomers')
        ) {
          void this.injector.get(AvailableCompaniesService).refresh();
        }
        return throwError(() => err);
      })
    );
  }
}
